import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import StarIcon from '@mui/icons-material/Star';
import { Helmet } from 'react-helmet-async';
import useCocktails, { Cocktail } from '../hooks/useCocktails';

const PLACEHOLDER_IMAGE = 'https://placehold.co/600x400?text=Cocktail';
const INGREDIENT_FIELD_LIMIT = 15;
const normalizeIngredientName = (value: string) => value.trim().toLowerCase();
const PAGE_URL = 'https://createadish.com/drinks';
const PAGE_TITLE = 'Cocktail Explorer - Create A Dish';
const PAGE_DESCRIPTION = 'Discover curated cocktail recipes filtered by alcoholic style, category, or favorite ingredients, then open detailed instructions instantly.';
const PAGE_IMAGE = 'https://createadish.com/og-drinks.png';

const collectIngredientNames = (drink: Cocktail) => {
  const names: string[] = [];
  for (let i = 1; i <= INGREDIENT_FIELD_LIMIT; i += 1) {
    const value = drink[`strIngredient${i}`];
    if (value) {
      names.push(value.trim());
    }
  }
  return names;
};

const buildIngredientLines = (drink: Cocktail) => {
  const list: string[] = [];
  for (let i = 1; i <= INGREDIENT_FIELD_LIMIT; i += 1) {
    const ingredient = drink[`strIngredient${i}`];
    if (!ingredient) {
      continue;
    }
    const measure = drink[`strMeasure${i}`];
    const formatted = measure ? `${measure.trim()} ${ingredient.trim()}` : ingredient.trim();
    list.push(formatted);
  }
  return list;
};

const Drinks: React.FC = () => {
  const {
    drinks,
    loading,
    error,
    categories,
    ingredientOptions,
    alcoholicOptions,
  } = useCocktails();
  const [selectedAlcoholic, setSelectedAlcoholic] = useState<string>('Alcoholic');
  const [selectedCategory, setSelectedCategory] = useState<string>('Popular');
  const [selectedIngredients, setSelectedIngredients] = useState<string[]>([]);
  const [activeDrink, setActiveDrink] = useState<Cocktail | null>(null);

  const structuredData = useMemo(() => {
    const highlights = drinks.slice(0, 6).map((drink, index) => {
      const recipeItem: Record<string, unknown> = {
        '@type': 'Recipe',
        name: drink.strDrink,
        url: `${PAGE_URL}#${drink.idDrink}`,
      };
      if (drink.strDrinkThumb) {
        recipeItem.image = drink.strDrinkThumb;
      }
      if (drink.strCategory) {
        recipeItem.recipeCategory = drink.strCategory;
      }
      if (drink.strAlcoholic) {
        recipeItem.keywords = drink.strAlcoholic;
      }
      return {
        '@type': 'ListItem',
        position: index + 1,
        item: recipeItem,
      };
    });

    return {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Cocktail Explorer',
      description: PAGE_DESCRIPTION,
      url: PAGE_URL,
      isPartOf: {
        '@type': 'WebSite',
        name: 'Create A Dish',
        url: 'https://createadish.com',
      },
      ...(highlights.length ? { hasPart: highlights } : {}),
    } as Record<string, unknown>;
  }, [drinks]);
  const structuredDataJson = useMemo(() => JSON.stringify(structuredData), [structuredData]);

  const handleIngredientToggle = (ingredient: string) => {
    setSelectedIngredients(prev => (
      prev.includes(ingredient)
        ? prev.filter(item => item !== ingredient)
        : [...prev, ingredient]
    ));
  };

  const filteredDrinks = useMemo(() => {
    const normalizedSelections = selectedIngredients.map(normalizeIngredientName);
    return drinks.filter(drink => {
    if (selectedAlcoholic && drink.strAlcoholic !== selectedAlcoholic) {
      return false;
    }
    if (selectedCategory === 'Popular') {
      if (!drink.isPopular) {
        return false;
      }
    } else if (selectedCategory && drink.strCategory !== selectedCategory) {
      return false;
    }

      if (selectedIngredients.length > 0) {
        const drinkIngredients = collectIngredientNames(drink).map(normalizeIngredientName);
        const hasAll = normalizedSelections.every(ingredient => drinkIngredients.includes(ingredient));
        if (!hasAll) {
          return false;
        }
      }
      return true;
    });
  }, [drinks, selectedAlcoholic, selectedCategory, selectedIngredients]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', mt: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, py: 4 }}>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="cocktail recipes, drink finder, gin cocktails, vodka cocktails, tequila drinks" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={PAGE_TITLE} />
        <meta property="twitter:description" content={PAGE_DESCRIPTION} />
        <meta property="twitter:image" content={PAGE_IMAGE} />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">{structuredDataJson}</script>
      </Helmet>

      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <LocalBarIcon color="primary" />
        <Typography variant="h4" fontWeight="bold">
          Cocktail Explorer
        </Typography>
      </Stack>

      <Typography variant="body1" color="text.secondary" mb={4}>
        Discover hundreds of cocktails curated from TheCocktailDB. Filter by style, category, or favorite ingredients,
        then dive into detailed recipes with one tap.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          mb: 4,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          alignItems: 'flex-start',
        }}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Alcoholic Style
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {alcoholicOptions.map(option => (
                <Chip
                  key={option}
                  label={option}
                  color={selectedAlcoholic === option ? 'primary' : 'default'}
                  variant={selectedAlcoholic === option ? 'filled' : 'outlined'}
                  onClick={() => setSelectedAlcoholic(option)}
                />
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
              Category
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {categories.map(category => (
                <Chip
                  key={category}
                  label={category}
                  color={selectedCategory === category ? 'secondary' : 'default'}
                  variant={selectedCategory === category ? 'filled' : 'outlined'}
                  onClick={() => setSelectedCategory(category)}
                />
              ))}
            </Box>
          </Box>
        </Stack>

        <Box sx={{ gridColumn: { md: 'span 2' } }}>
          <Typography variant="subtitle1" fontWeight={600} mb={1}>
            Popular Ingredients
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {ingredientOptions.map(ingredient => (
              <Chip
                key={ingredient}
                label={ingredient}
                color={selectedIngredients.includes(ingredient) ? 'success' : 'default'}
                variant={selectedIngredients.includes(ingredient) ? 'filled' : 'outlined'}
                onClick={() => handleIngredientToggle(ingredient)}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" fontWeight={600}>
          Showing {filteredDrinks.length} cocktails
        </Typography>
        <Button onClick={() => setSelectedIngredients([])} disabled={selectedIngredients.length === 0}>
          Clear ingredient filters
        </Button>
      </Stack>

      {filteredDrinks.length === 0 ? (
        <Alert severity="info">No cocktails match the current filters. Try removing one of the filters.</Alert>
      ) : (
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' } }}>
          {filteredDrinks.map(drink => (
            <Box key={drink.idDrink}>
              <Card sx={{ height: '100%' }}>
                <CardActionArea onClick={() => setActiveDrink(drink)} sx={{ height: '100%' }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={drink.strDrinkThumb || PLACEHOLDER_IMAGE}
                    alt={drink.strDrink}
                  />
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="h6" fontWeight={600}>
                        {drink.strDrink}
                      </Typography>
                      {drink.isPopular && (
                        <Chip
                          icon={<StarIcon />}
                          label="Popular"
                          size="small"
                          color="warning"
                        />
                      )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {drink.strCategory} · {drink.strGlass}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {drink.strAlcoholic}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={Boolean(activeDrink)} onClose={() => setActiveDrink(null)} maxWidth="sm" fullWidth>
        {activeDrink && (
          <>
            <DialogTitle>{activeDrink.strDrink}</DialogTitle>
            <DialogContent dividers>
              <Stack direction="row" spacing={2} mb={2}>
                <Box flexShrink={0}>
                  <Box component="img"
                    src={activeDrink.strDrinkThumb || PLACEHOLDER_IMAGE}
                    alt={activeDrink.strDrink}
                    sx={{ width: 160, borderRadius: 1 }}
                  />
                </Box>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    {activeDrink.strCategory}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {activeDrink.strAlcoholic}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Served in {activeDrink.strGlass}
                  </Typography>
                </Stack>
              </Stack>

              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Ingredients
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                {buildIngredientLines(activeDrink).map((line, index) => (
                  <Typography component="li" key={`${line}-${index}`} variant="body2">
                    {line}
                  </Typography>
                ))}
              </Box>

              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Instructions
              </Typography>
              <DialogContentText component="div" sx={{ whiteSpace: 'pre-line' }}>
                {activeDrink.strInstructions || 'No instructions available.'}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setActiveDrink(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Drinks;
